import { useTranslation } from 'react-i18next'
import { getModule } from '@/modules/registry'
import type { RowContext } from '@/modules/layouts/rows'
import { useCommunity } from '@/lib/CommunityContext'
import { useRoute } from '@/lib/RouteContext'
import { isSpaceLayout, type SpaceLayout } from '@/types/spaceLayout'

/**
 * F-198: the layout a space page draws. The space's own choice when the module can render it;
 * otherwise (`null`, absent, or a value this module does not draw, such as `showcase` outside
 * Courses) the module's default, which is what the page always drew.
 */
export const resolveSpaceLayout = (space: { layout?: unknown } | null | undefined, moduleKey: string): SpaceLayout => {
  const support = getModule(moduleKey)?.layouts
  const fallback = support?.fallback ?? 'cards'
  const chosen = space?.layout
  return isSpaceLayout(chosen) && support?.renders.includes(chosen) ? chosen : fallback
}

/** The layouts "Exibição" may offer: what the API allows for the module and the app can draw. */
export const offeredLayouts = (moduleKey: string, apiOptions: SpaceLayout[] | null | undefined): SpaceLayout[] => {
  const renders = getModule(moduleKey)?.layouts?.renders ?? []
  return (apiOptions ?? []).filter((layout) => renders.includes(layout))
}

/**
 * What the row adapters need (`modules/layouts/rows.ts`): the translator, the reader's language, the
 * community's zone and `/{section}/{space}`, the base of an item's page.
 */
export const useRowContext = (): RowContext => {
  const { t, i18n } = useTranslation()
  const { community } = useCommunity()
  const { params, spaceData } = useRoute()
  const sectionSlug = params.sectionSlug || spaceData?.sectionSlug
  const spaceSlug = params.spaceSlug || spaceData?.slug
  const basePath = sectionSlug && spaceSlug ? `/${sectionSlug}/${spaceSlug}` : (typeof window !== 'undefined' ? window.location.pathname.replace(/\/$/, '') : '')
  return { t, locale: i18n.language || 'pt-BR', timeZone: community?.timezone || undefined, basePath }
}
